import type { FontFamily } from 'packages/types/fonts';
import {
    actionTypes,
    DefaultFontChange,
    FontActivationToggle,
    FontDeletion,
    FontList,
    FontListQuery,
    FontManagementState,
    FontReplacement,
    FontUpload,
} from './types';
import { DEFAULT_FONT_LIST_QUERY } from './query';

const initialState: FontManagementState = {
    list: { status: 'not started' },
    query: DEFAULT_FONT_LIST_QUERY,
    upload: undefined,
    deletion: undefined,
    toggleActivation: undefined,
    defaultChange: undefined,
    replacement: undefined,
    isEditorFontsStale: false,
};

export default function fontManagement(state = initialState, action): FontManagementState {
    const next = {
        list: list(state.list, action),
        query: query(state.query, action),
        upload: upload(state.upload, action),
        deletion: deletion(state.deletion, action),
        toggleActivation: toggleActivation(state.toggleActivation, action),
        defaultChange: defaultChange(state.defaultChange, action),
        replacement: replacement(state.replacement, action),
        isEditorFontsStale: isEditorFontsStale(state.isEditorFontsStale, action),
    };

    return Object.keys(next).some((key) => next[key] !== state[key]) ? next : state;
}

const knownFontIds = (families: FontFamily[], fontIds: number[]): number[] =>
    fontIds.filter((id) => families.some((family) => family.fonts.some((font) => font.id === id)));

function list(state: FontList, action): FontList {
    switch (action.type) {
        case actionTypes.GET_FONT_FAMILIES_REQUEST:
            return { status: 'loading', loaded: state.loaded };
        case actionTypes.GET_FONT_FAMILIES_SUCCESS:
            return {
                status: 'ready',
                loaded: {
                    families: action.families,
                    query: action.query,
                    loadedAt: action.loadedAt,
                    selectedFontIds: knownFontIds(action.families, state.loaded?.selectedFontIds ?? []),
                },
            };
        case actionTypes.GET_FONT_FAMILIES_FAILURE:
            return { status: 'error', errors: action.errors, loaded: state.loaded };
        case actionTypes.SET_DEFAULT_FONT_SUCCESS:
            return state.status !== 'not started' && state.loaded
                ? {
                      ...state,
                      loaded: {
                          ...state.loaded,
                          families: state.loaded.families.map((family) => {
                              const fonts = family.fonts.map((font) => ({
                                  ...font,
                                  isDefault: font.id === action.fontId,
                              }));

                              return { ...family, fonts, hasDefault: fonts.some((font) => font.isDefault) };
                          }),
                      },
                  }
                : state;
        case actionTypes.FONT_SELECTION_TOGGLED: {
            if (state.status === 'not started' || !state.loaded) {
                return state;
            }

            const { selectedFontIds } = state.loaded;
            const fontIds = knownFontIds(state.loaded.families, action.fontIds);
            const next = action.isSelected
                ? [...selectedFontIds, ...fontIds.filter((id) => !selectedFontIds.includes(id))]
                : selectedFontIds.filter((id) => !fontIds.includes(id));

            return next.length === selectedFontIds.length
                ? state
                : { ...state, loaded: { ...state.loaded, selectedFontIds: next } };
        }
        case actionTypes.FONT_SELECTION_CLEARED:
        case actionTypes.FONT_LIST_QUERY_CHANGED:
            return state.status !== 'not started' && state.loaded?.selectedFontIds.length
                ? { ...state, loaded: { ...state.loaded, selectedFontIds: [] } }
                : state;
        case actionTypes.DELETE_FONTS_SUCCESS:
            return state.status !== 'not started' && state.loaded
                ? {
                      ...state,
                      loaded: {
                          ...state.loaded,
                          families: state.loaded.families.flatMap((family) => {
                              const fonts = family.fonts.filter((font) => !action.fontIds.includes(font.id));

                              if (!fonts.length) {
                                  return [];
                              }

                              return [
                                  {
                                      ...family,
                                      fonts,
                                      fontCount: family.fontCount - (family.fonts.length - fonts.length),
                                      hasDefault: fonts.some((font) => font.isDefault),
                                  },
                              ];
                          }),
                          selectedFontIds: [],
                      },
                  }
                : state;
        case actionTypes.TOGGLE_ACTIVATE_FONTS_SUCCESS: {
            const shouldDeactivate = action.mode === 'deactivate';
            const idSet = new Set(action.fontIds);

            return state.status !== 'not started' && state.loaded
                ? {
                      ...state,
                      loaded: {
                          ...state.loaded,
                          families: state.loaded.families.map((family) => ({
                              ...family,
                              fonts: family.fonts.map((font) => {
                                  if (!idSet.has(font.id)) {
                                      return font;
                                  }

                                  if (shouldDeactivate && font.isActive) {
                                      return { ...font, isActive: false };
                                  }

                                  if (!shouldDeactivate && !font.isActive) {
                                      return { ...font, isActive: true };
                                  }

                                  return font;
                              }),
                          })),
                      },
                  }
                : state;
        }
        default:
            return state;
    }
}

function query(state: FontListQuery, action): FontListQuery {
    return action.type === actionTypes.FONT_LIST_QUERY_CHANGED ? action.query : state;
}

function defaultChange(state: DefaultFontChange | undefined, action): DefaultFontChange | undefined {
    switch (action.type) {
        case actionTypes.DEFAULT_FONT_CHANGE_REQUESTED:
            return state === undefined ? { status: 'confirming', fontId: action.fontId } : state;
        case actionTypes.DEFAULT_FONT_CHANGE_CANCELLED:
            return state?.status === 'confirming' ? undefined : state;
        case actionTypes.SET_DEFAULT_FONT_REQUEST:
            return state?.status === 'confirming' ? { ...state, status: 'saving' } : state;
        case actionTypes.SET_DEFAULT_FONT_SUCCESS:
        case actionTypes.SET_DEFAULT_FONT_FAILURE:
            return state?.status === 'saving' ? undefined : state;
        default:
            return state;
    }
}

function replacement(state: FontReplacement | undefined, action): FontReplacement | undefined {
    switch (action.type) {
        case actionTypes.REPLACE_FONT_REQUESTED:
            return state === undefined
                ? { status: 'choosing', font: action.font, candidates: { status: 'loading' } }
                : state;
        case actionTypes.REPLACE_FONT_CANDIDATES_SUCCESS:
            if (state?.font.id !== action.fontId || state.candidates.status !== 'loading') {
                return state;
            }

            return state.status === 'choosing' &&
                state.font.isActive &&
                !knownFontIds(action.families, [state.font.id]).length
                ? undefined
                : { ...state, candidates: { status: 'ready', families: action.families } };
        case actionTypes.REPLACE_FONT_CANDIDATES_FAILURE:
            if (state?.font.id !== action.fontId || state.candidates.status !== 'loading') {
                return state;
            }

            return state.status === 'choosing' ? undefined : { ...state, candidates: { status: 'error' } };
        case actionTypes.REPLACE_FONT_CANCELLED:
            return state?.status === 'choosing' || state?.status === 'confirming' ? undefined : state;
        case actionTypes.REPLACE_FONT_REQUEST:
            return state?.status === 'choosing' || state?.status === 'confirming'
                ? { ...state, status: 'replacing' }
                : state;
        case actionTypes.REPLACE_FONT_OVERWRITE_REQUESTED:
            return state?.status === 'replacing'
                ? { ...state, status: 'confirming', overwrite: action.overwrite }
                : state;
        case actionTypes.REPLACE_FONT_OVERWRITE_DECLINED:
            return state?.status === 'confirming'
                ? { status: 'choosing', font: state.font, candidates: state.candidates }
                : state;
        case actionTypes.REPLACE_FONT_SUCCESS:
            return state?.status === 'replacing' ? undefined : state;
        case actionTypes.REPLACE_FONT_FAILURE:
            return state?.status === 'replacing'
                ? { status: 'choosing', font: state.font, candidates: state.candidates }
                : state;
        default:
            return state;
    }
}

function deletion(state: FontDeletion | undefined, action): FontDeletion | undefined {
    switch (action.type) {
        case actionTypes.DELETE_FONTS_REQUESTED:
            return state === undefined && action.fontIds.length
                ? { status: 'confirming', scope: action.scope, fontIds: action.fontIds }
                : state;
        case actionTypes.DELETE_FONTS_CANCELLED:
            return state?.status === 'confirming' ? undefined : state;
        case actionTypes.DELETE_FONTS_REQUEST:
            return state?.status === 'confirming' ? { ...state, status: 'deleting' } : state;
        case actionTypes.DELETE_FONTS_SUCCESS:
        case actionTypes.DELETE_FONTS_FAILURE:
            return state?.status === 'deleting' ? undefined : state;
        default:
            return state;
    }
}

function toggleActivation(state: FontActivationToggle | undefined, action): FontActivationToggle | undefined {
    switch (action.type) {
        case actionTypes.TOGGLE_ACTIVATE_FONTS_REQUESTED:
            return state === undefined && action.fontIds.length
                ? { status: 'confirming', mode: action.mode, scope: action.scope, fontIds: action.fontIds }
                : state;
        case actionTypes.TOGGLE_ACTIVATE_FONTS_CANCELLED:
            return state?.status === 'confirming' ? undefined : state;
        case actionTypes.TOGGLE_ACTIVATE_FONTS_REQUEST:
            return state?.status === 'confirming' ? { ...state, status: 'toggle_activation' } : state;
        case actionTypes.TOGGLE_ACTIVATE_FONTS_SUCCESS:
        case actionTypes.TOGGLE_ACTIVATE_FONTS_FAILURE:
            return state?.status === 'toggle_activation' ? undefined : state;
        default:
            return state;
    }
}

function upload(state: FontUpload | undefined, action): FontUpload | undefined {
    switch (action.type) {
        case actionTypes.UPLOAD_FONTS_STARTED:
            return { status: 'checking', total: action.total };
        case actionTypes.UPLOAD_REPLACE_REQUESTED:
            return state?.status === 'checking'
                ? { status: 'confirming replace', files: action.files, checkFailures: action.checkFailures }
                : state;
        case actionTypes.UPLOAD_FONTS_UPLOADING:
            return state?.status === 'checking' || state?.status === 'confirming replace'
                ? {
                      status: 'uploading',
                      total: action.total,
                      completed: action.completed,
                      failedFiles: action.failedFiles,
                      replaced: 0,
                  }
                : state;
        case actionTypes.UPLOAD_FONT_SUCCESS:
            return state?.status === 'uploading'
                ? {
                      ...state,
                      completed: state.completed + 1,
                      replaced: action.replaced ? state.replaced + 1 : state.replaced,
                  }
                : state;
        case actionTypes.UPLOAD_FONT_FAILURE:
            return state?.status === 'uploading'
                ? { ...state, completed: state.completed + 1, failedFiles: [...state.failedFiles, action.file] }
                : state;
        case actionTypes.UPLOAD_FONTS_FINISHED:
            return undefined;
        default:
            return state;
    }
}

function isEditorFontsStale(state: boolean, action): boolean {
    switch (action.type) {
        case actionTypes.SET_DEFAULT_FONT_SUCCESS:
        case actionTypes.UPLOAD_FONT_SUCCESS:
        case actionTypes.DELETE_FONTS_SUCCESS:
        case actionTypes.REPLACE_FONT_UPLOADED:
        case actionTypes.REPLACE_FONT_SUCCESS:
        case actionTypes.TOGGLE_ACTIVATE_FONTS_SUCCESS:
            return true;
        case actionTypes.EDITOR_FONTS_SYNCED:
            return false;
        default:
            return state;
    }
}
